import React from 'react'
import {
  Modal,
  ModalClose,
  ModalContent,
  Tile,
  TileImage,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { TrackContentType } from '@mc/track-event'
import { VideoAutoplay, VideoEventHandlers, VideoOverlay } from '@mc/video'
import Image from 'components/LegacyImage'
import SubscribeVideoScreen from 'components/VideoModal/SubscribeVideoScreen'
import { VideoPlayer } from 'components/VideoPlayer'
import ContactSalesVideoScreen from './ContactSalesVideoScreen'

interface Props {
  show?: boolean
  isSample?: boolean
  isEnterprise?: boolean
  closeModalOnPauseScreenCTAClick?: boolean
  canClickOutsideToClose?: boolean
  onCloseModal?: (source?: string, event?: Event | React.SyntheticEvent) => void
  videoId?: string | null
  mediaUuid?: string
  courseId?: string | number
  modalTitle?: string | null
  contentId: string | number
  contentTitle: string
  contentType: TrackContentType
  tagline?: string | null
  instructorAvatar?: string | null
  buttonGroup?: React.ReactNode
  onPlay?: () => void
  onPause?: () => void
  onEnd?: () => void
  onSeek?: () => void
  onSeeked?: () => void
  onTimeChange?: () => void
  signUpHref?: string
  trackProps?: {
    uuid?: string
    categorySlug?: string
  }
}

const VideoModal = ({
  show,
  isSample = false,
  isEnterprise = false,
  closeModalOnPauseScreenCTAClick = false,
  canClickOutsideToClose = true,
  onCloseModal,
  videoId,
  mediaUuid,
  courseId,
  modalTitle,
  contentId,
  contentTitle,
  contentType,
  tagline,
  instructorAvatar,
  buttonGroup,
  onPlay,
  onPause,
  onEnd,
  onSeek,
  onSeeked,
  onTimeChange,
  signUpHref = '',
  trackProps = {},
}: Props) => {
  const { t } = useTranslation('@mc/next')
  const modalHeadingKey = isSample
    ? 'course.sample.modal.heading'
    : 'course.trailer.modal.heading'

  if (!videoId && !mediaUuid) return <></>

  const renderPauseScreen = () =>
    isEnterprise ? (
      <ContactSalesVideoScreen
        isSample={isSample}
        onCTAClick={closeModalOnPauseScreenCTAClick ? onCloseModal : undefined}
      />
    ) : (
      <SubscribeVideoScreen
        isSample={isSample}
        signUpHref={signUpHref}
        trackProps={trackProps}
        chapterImage={undefined}
      />
    )

  return (
    <Modal
      backdrop='extra-dark'
      show={show}
      onClose={onCloseModal}
      canClickOutsideToClose={canClickOutsideToClose}
    >
      <ModalClose />
      <ModalContent aria-labelledby='trailer-modal-heading'>
        <div
          className='row justify-content-between align-items-center mc-mb-2 mc-overflow--hidden'
          data-testid='trailer-modal'
        >
          {instructorAvatar && (
            <div className='d-none d-md-block col-2 col-md-1'>
              <Tile aspectRatio='1x1'>
                <TileImage
                  className='mc-corners--circle'
                  image={<Image alt='' src={instructorAvatar} layout='fill' />}
                />
              </Tile>
            </div>
          )}

          <div className='col'>
            <h1
              className='mc-text-h5 mc-text--1-line-max'
              id='trailer-modal-heading'
            >
              {t(modalHeadingKey, { title: modalTitle })}
            </h1>
            <p className='mc-mt-1'>{tagline}</p>
          </div>

          <div className='col-auto'>{buttonGroup}</div>
        </div>
        <VideoPlayer
          contentId={contentId}
          contentTitle={contentTitle}
          contentType={contentType}
          courseId={courseId}
          videoId={videoId ?? undefined}
          mediaUuid={mediaUuid}
        >
          <VideoEventHandlers
            onPlay={onPlay}
            onPause={onPause}
            onEnded={onEnd}
            onSeeking={onSeek}
            onSeeked={onSeeked}
            onTimeUpdate={onTimeChange}
          />
          <VideoAutoplay />
          <VideoOverlay state={['paused', 'ended']} fade>
            {renderPauseScreen()}
          </VideoOverlay>
        </VideoPlayer>
      </ModalContent>
    </Modal>
  )
}

export default VideoModal
